'use client'
import { useQuery } from '@tanstack/react-query'
import React from 'react'
import accountApi from '~/apis/account'
import authApi from '~/apis/auth'
import { User } from '~/types/account'

type UserContextType = [
    user: User | undefined,
    setUser: React.Dispatch<React.SetStateAction<User | undefined>>,
    isLoading: boolean
]

const UserContext = React.createContext<UserContextType>([undefined, () => {}, true])

const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [mounted, setMounted] = React.useState(false)

    React.useEffect(() => {
        setMounted(true)
    }, [])

    const userQuery = useQuery({
        queryKey: ['me'],
        queryFn: () => accountApi.me(),
        enabled: mounted,
        retry: 0,
        refetchOnWindowFocus: false
    })
    const [isLoading, setIsLoading] = React.useState(true)
    const [user, setUser] = React.useState<User | undefined>(undefined)

    React.useEffect(() => {
        if (!mounted) return

        if (userQuery.data) {
            setUser(userQuery.data.data)
        }
        if (userQuery.isError) {
            authApi.logoutNext()
            setUser(undefined)
        }
        setIsLoading(userQuery.isPending)
    }, [mounted, userQuery.data, userQuery.isPending, userQuery.isError])

    return <UserContext.Provider value={[user, setUser, isLoading]}>{children}</UserContext.Provider>
}

export default UserProvider

export const useUser = () => {
    const context = React.useContext(UserContext)
    return context
}
